import { useState } from 'react';
import { useRef } from 'react';
import { Button } from '~/components/ui/button';
import { cn } from '~/utils/misc';
import { useDebounce } from '~/utils/misc';

export default function StorageScholarsRoute() {
  return (
    <div className="host-theme flex flex-col items-center">
      <div className="relative h-44 w-full overflow-hidden md:h-52 lg:h-56 xl:h-[20vw]">
        <img
          src="/img/landing/storage-scholars/bg.jpeg"
          alt="Storage Scholars Display"
          className=" mx-auto size-full w-full object-cover"
        />
        <img
          src="/img/landing/storage-scholars/bg.jpeg"
          alt="Storage Scholars Bg"
          className="-z-10 absolute top-0 bottom-0 hidden h-full w-[120vw] bg-black object-cover blur-2xl xl:block"
        />
      </div>
      <div className="w-full max-w-6xl px-6 py-16 md:px-20">
        <p className="text-3xl md:text-5xl">Moving made easy with</p>
        <p className="font-semibold text-3xl md:text-5xl">Storage Scholars</p>
        <div className="h-8" />
        <p className="md:text-xl">
          At hostU, we know that moving can be a hassle — so we’ve partnered with Storage Scholars
          to make the process easier than ever.
        </p>
        <div className="h-8" />
        <Button className="px-8 text-lg md:px-16" asChild>
          <a href="https://storagesch.com/17261622370355" target="_blank" rel="noreferrer">
            Book now
          </a>
        </Button>
      </div>
      <HowItWorksSection />
      <div className="host-theme flex w-full items-center justify-center bg-primary px-6 py-14 lg:px-8">
        <p className="w-full max-w-[1280px] font-semibold text-white text-xl sm:text-2xl">
          Keep it secure with <br className="sm:hidden" /> students helping students.
        </p>
      </div>

      <div className="flex w-full items-center justify-center py-8 md:py-16">
        <div className="flex w-full max-w-[1200px] items-center justify-evenly">
          <img
            src="/img/icons/house.png"
            alt="Handshake"
            className="size-10 sm:size-12 md:size-16 lg:size-20"
          />
          <img
            src="/img/icons/handshake.png"
            alt="Handshake"
            className="size-10 md:size-12 lg:size-14"
          />
          <img
            src="/img/icons/box.png"
            alt="Handshake"
            className="size-10 sm:size-12 md:size-16 lg:size-20"
          />
        </div>
      </div>
    </div>
  );
}

const howItWorksSlides = [
  <HowItWorksStep
    key="slide-1"
    className="flex-1"
    imageUrl="/img/landing/storage-scholars/step-1"
    title="Step 1: Supply"
    description="Free packing supplies with every purchase is sent right to your door"
  />,
  <HowItWorksStep
    key="slide-2"
    className="flex-1"
    imageUrl="/img/landing/storage-scholars/step-2"
    title="Step 2: Move"
    description="Door to door pickup and delivery from student movers you know and trust"
  />,
  <HowItWorksStep
    key="slide-3"
    className="flex-1"
    imageUrl="/img/landing/storage-scholars/step-3"
    title="Step 3: Storage"
    description="All of your items will be secured in climate controlled storage units during your time away"
  />,
  <HowItWorksStep
    key="slide-4"
    className="flex-1"
    imageUrl="/img/landing/storage-scholars/step-4"
    title="Step 4: Ship"
    description="Ship your items home, to your next sublet, or back for the next semester—stress-free."
  />,
];

type HowItWorksStepProps = {
  imageUrl: string;
  title: string;
  description: string;
  className?: string;
};
export function HowItWorksStep(props: HowItWorksStepProps) {
  const { title, description, imageUrl, className } = props;

  return (
    <div
      className={cn(
        'flex min-w-full snap-start flex-col items-center gap-2 rounded-lg bg-white px-8 py-8 text-center text-black md:min-w-0',
        className,
      )}
    >
      <picture>
        <source type="image/webp" srcSet={`${imageUrl}.webp`} />
        <img
          src={`${imageUrl}.jpg`}
          alt={title}
          className="aspect-square h-full w-full rounded-lg object-cover"
        />
      </picture>
      <div className="h-1" />
      <p className="flex items-center gap-2 font-semibold text-xl xl:text-2xl">{title}</p>
      <div className="h-1" />
      <div className="min-h-28 xs:min-h-16 sm:min-h-16 md:min-h-16 lg:min-h-28 xl:min-h-16">
        <p className="leading-5">{description}</p>
      </div>
    </div>
  );
}

function HowItWorksSection() {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const handleScroll = useDebounce((e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setSlideIndex(
      Math.max(
        Math.min(
          Math.floor((container.scrollLeft / container.scrollWidth) * howItWorksSlides.length),
          howItWorksSlides.length - 1,
        ),
        0,
      ),
    );
  }, 30);

  return (
    <section className="flex w-full items-center justify-center bg-hostU-blue-gradient px-8 py-10 md:px-8 md:py-20">
      <div className="w-full max-w-[1280px] text-white">
        <p className="font-semibold text-2xl">How it works</p>
        <div
          className="hide-scrollbar flex snap-x snap-mandatory grid-cols-2 grid-rows-2 gap-4 overflow-x-auto overflow-y-hidden pt-4 pb-3 sm:grid md:flex-row md:overflow-hidden md:pt-6 lg:flex"
          onScroll={handleScroll}
          ref={scrollContainerRef}
        >
          {howItWorksSlides}
        </div>
        <div className="flex items-center justify-center gap-2 sm:hidden">
          {howItWorksSlides.map((_, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
              key={i}
              className={cn(
                'h-2 w-2 rounded-full bg-white',
                i === slideIndex ? 'opacity-100' : 'opacity-50',
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
